import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import apiService from '../../services/api.service';
import GoldenArrow from './GoldenArrow';

type CopyPRDialogProps = {
  open: boolean;
  onClose: () => void;
  onSelectPR: (prId: number) => void;
  postedOnly?: boolean;
  title?: string;
};

const toDDMMYYYY = (v: any): string => {
  const s = String(v || '').trim();
  if (!s) return '';
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  const slash = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slash) return `${slash[1]}-${slash[2]}-${slash[3]}`;
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }
  return s;
};

const CopyPRDialog: React.FC<CopyPRDialogProps> = ({ open, onClose, onSelectPR, postedOnly = false, title = 'Copy from Previous PR' }) => {
  const [prs, setPrs] = useState<any[]>([]);
  const [filteredPrs, setFilteredPrs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredPrs(prs);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = prs.filter((pr) => {
        const prNumber = String(pr.prnumber || '').toLowerCase();
        const allocCode = String(pr.allocationcode || '').toLowerCase();
        const type = String(pr.requisitiontype || '').toLowerCase();
        return prNumber.includes(query) || allocCode.includes(query) || type.includes(query);
      });
      setFilteredPrs(filtered);
    }
  }, [searchQuery, prs]);

  const loadPRs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getMyPRs();
      const allPrs = (response as any)?.data || response || [];

      console.log('Fetched PRs:', allPrs);
      if (allPrs[0]) {
        console.log('First PR data:', allPrs[0]);
        console.log('Project Name:', allPrs[0].projectname || allPrs[0].ProjectName);
        console.log('Created By:', allPrs[0].requestedbyname);
        console.log('SAP DocNum:', allPrs[0].sappodocnum);
      }

      if (!Array.isArray(allPrs)) {
        console.error('PRs data is not an array:', allPrs);
        setError('Invalid data format received');
        setPrs([]);
        setFilteredPrs([]);
        return;
      }

      if (allPrs.length === 0) {
        console.log('No PRs found');
        setPrs([]);
        setFilteredPrs([]);
        return;
      }

      const eligible = allPrs.filter((pr: any) => {
        if (!postedOnly) return true;
        const status = String(pr.status || pr.Status || '').toLowerCase();
        const hasSapDoc = Boolean(pr.sappodocnum || pr.SAPPODocNum || pr.sappodocentry || pr.SAPPODocEntry);
        const prTotalQty = Number(pr.prtotalqty || pr.PRTotalQty || 0);
        const poCopiedQty = Number(pr.pocopiedqty || pr.POCopiedQty || 0);
        const openQtyTotal = Math.max(prTotalQty - poCopiedQty, 0);
        return (status.includes('postedtosap') || status === 'convertedtopo' || hasSapDoc) && openQtyTotal > 0;
      });

      const sorted = eligible
        .filter((pr: any) => pr && (pr.prid || pr.PRID))
        .sort((a: any, b: any) => {
          const dateA = new Date(a.prdate || a.PRDate || a.createddate || 0).getTime();
          const dateB = new Date(b.prdate || b.PRDate || b.createddate || 0).getTime();
          return dateB - dateA;
        })
        .slice(0, 20);

      const withIds = sorted.map((pr: any, idx: number) => {
        // Get project name from the backend JOIN
        const projectName = pr.projectname || pr.ProjectName || '';

        const createdBy = pr.requestedbyname || pr.RequestedByName || '';

        return {
          ...pr,
          id: pr.prid || pr.PRID || idx,
          prid: pr.prid || pr.PRID,
          prnumber: pr.prnumber || pr.PRNumber,
          prdate: pr.prdate || pr.PRDate,
          requisitiontype: pr.requisitiontype || pr.RequisitionType,
          allocationtype: pr.allocationtype || pr.AllocationType,
          allocationcode: pr.allocationcode || pr.AllocationCode,
          allocationname: projectName,
          totalamount: pr.totalamount || pr.TotalAmount,
          currency: pr.currency || pr.Currency || 'SAR',
          requestedbyname: createdBy,
          sappodocnum: pr.sappodocnum || pr.SAPPODocNum || null,
          openqtytotal: Math.max(Number(pr.prtotalqty || pr.PRTotalQty || 0) - Number(pr.pocopiedqty || pr.POCopiedQty || 0), 0)
        };
      });

      console.log('Processed PRs for display:', withIds);
      setPrs(withIds);
      setFilteredPrs(withIds);
    } catch (err: any) {
      setError('Failed to load PRs. Please try again.');
      console.error('Error loading PRs:', err);
      setPrs([]);
      setFilteredPrs([]);
    } finally {
      setLoading(false);
    }
  }, [postedOnly]);

  useEffect(() => {
    if (open) {
      void loadPRs();
      setSearchQuery('');
    }
  }, [open, loadPRs]);

  const handleRowClick = (params: any) => {
    const prId = Number(params.row.prid || params.id);
    if (prId) {
      onSelectPR(prId);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'prnumber',
      headerName: 'PR Number',
      width: 160,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GoldenArrow prId={params.row.prid} size="small" />
          <Typography variant="body2" fontWeight={600}>
            {params.value}
          </Typography>
        </Box>
      )
    },
    {
      field: 'prdate',
      headerName: 'Date',
      width: 110,
      valueFormatter: (params) => toDDMMYYYY(params.value)
    },
    {
      field: 'requisitiontype',
      headerName: 'Type',
      width: 100
    },
    {
      field: 'allocationtype',
      headerName: 'Allocation',
      width: 100
    },
    {
      field: 'allocationcode',
      headerName: 'Code',
      width: 120
    },
    {
      field: 'allocationname',
      headerName: 'Project Name',
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2" noWrap title={params.value}>
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'requestedbyname',
      headerName: 'Created By',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" noWrap title={params.value}>
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'sappodocnum',
      headerName: 'SAP DocNum',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={params.value ? 600 : 400} color={params.value ? 'success.main' : 'text.secondary'}>
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'openqtytotal',
      headerName: 'Open Qty',
      width: 110,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (params) => Number(params.value || 0).toFixed(4)
    },
    {
      field: 'totalamount',
      headerName: 'Amount',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (params) => {
        const value = Number(params.value || 0);
        return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
    }
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, mt: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by PR Number, Allocation Code, or Type..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredPrs.length === 0 && !error ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {postedOnly ? 'No Posted PRs Available' : 'No PRs Available'}
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {postedOnly ? 'No posted PR with remaining open quantity was found for your user/company selection.' : 'You need to create at least one PR before you can copy from it.'}
              <br />
              Click "Create PR" to create your first purchase requisition.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={filteredPrs}
              columns={columns}
              onRowClick={handleRowClick}
              pageSizeOptions={[10, 20]}
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } }
              }}
              sx={{
                '& .MuiDataGrid-row': {
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopyPRDialog;
